import { Link, useParams } from 'react-router-dom';
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../../lib/api';
import { TextField } from '../../components/TextField';
import { useMatchdays, useMatchdayStatus } from '../../features/matchdays/api';
import {
  useLeague,
  useLeagueMembers,
  useLeagueInvites,
  usePermanentInvite,
  useCreateInvite,
  useRevokeInvite,
  useUpdateMember,
} from '../../features/leagues/api';
import { useAnnouncer } from '../../components/LiveAnnouncer';
import { usePageTitle } from '../../lib/usePageTitle';
import { AppLayout } from '../../components/AppLayout';
import { Button } from '../../components/Button';
import { LeagueNav } from './LeagueNav';

export function LeagueDetailPage() {
  const { id = '' } = useParams();
  const league = useLeague(id);
  usePageTitle(league.data?.name ?? 'Lega');

  const isAdmin = league.data?.myRole === 'ADMIN' || league.data?.myRole === 'SUPER_ADMIN';

  if (league.isLoading) {
    return (
      <AppLayout title="Lega">
        <p role="status" className="mt-4">
          Caricamento della lega…
        </p>
      </AppLayout>
    );
  }

  if (league.isError || !league.data) {
    return (
      <AppLayout title="Lega non trovata">
        <p role="alert" className="mt-4 font-medium text-danger">
          Questa lega non esiste oppure non ne fai parte.
        </p>
      </AppLayout>
    );
  }

  const l = league.data;

  return (
    <AppLayout title={l.name}>
      <LeagueNav leagueId={id} />
      <p className="mt-4 text-lg text-ink-700 dark:text-ink-200">
        {l.competition} {l.seasonLabel} · {l.memberCount} di {l.maxParticipants} partecipanti
        {l.ruleset && ` · Regolamento ${l.ruleset.name} v${l.ruleset.version}`}
      </p>

      {isAdmin && <AdminSummarySection leagueId={id} />}
      {isAdmin && <AutomationSection leagueId={id} automation={l.automation} />}
      <TopScorerSection leagueId={id} isAdmin={isAdmin} />
      <DisputesSection leagueId={id} isAdmin={isAdmin} />
      <MembersSection leagueId={id} isAdmin={isAdmin} />
      {isAdmin && <InvitesSection leagueId={id} leagueName={l.name} />}
    </AppLayout>
  );
}

/** Automazioni di lega: deadline automatica e calcolo automatico (admin). */
function AutomationSection({
  leagueId,
  automation,
}: {
  leagueId: string;
  automation: {
    autoDeadline: boolean;
    autoDeadlineMinutesBefore: number;
    autoScoring: boolean;
    autoCreate: boolean;
  };
}) {
  const { announce } = useAnnouncer();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState(automation);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dirty =
    draft.autoDeadline !== automation.autoDeadline ||
    draft.autoDeadlineMinutesBefore !== automation.autoDeadlineMinutesBefore ||
    draft.autoScoring !== automation.autoScoring ||
    draft.autoCreate !== automation.autoCreate;

  return (
    <section aria-labelledby="automazioni" className="mt-8 rounded-2xl border border-ink-200 p-5 dark:border-ink-800">
      <h2 id="automazioni" className="text-xl font-bold tracking-tight">
        Automazioni (amministrazione)
      </h2>
      <p className="mt-1 text-ink-700 dark:text-ink-200">
        Con le automazioni attive la lega va avanti da sola; puoi sempre intervenire a mano
        (risultati, ricalcoli, correzioni).
      </p>
      {error && (
        <p role="alert" className="mt-3 font-medium text-danger">{error}</p>
      )}

      <div className="mt-4 flex flex-col gap-4">
        <label className="flex cursor-pointer items-start gap-3">
          <input
            type="checkbox"
            className="mt-1 h-5 w-5 accent-panda-700"
            checked={draft.autoDeadline}
            onChange={(e) => setDraft({ ...draft, autoDeadline: e.target.checked })}
          />
          <span>
            <span className="font-semibold">Chiusura pronostici automatica</span>
            <span className="block text-sm text-ink-600 dark:text-ink-300">
              Niente date da inserire: la giornata si apre subito e i pronostici si chiudono da
              soli prima del calcio d’inizio della prima partita. Se il calendario slitta, la
              chiusura si adegua.
            </span>
          </span>
        </label>
        {draft.autoDeadline && (
          <label className="ml-8 flex items-center gap-3">
            <span className="font-medium">Chiudi</span>
            <input
              type="number"
              min={5}
              max={1440}
              className="w-24 rounded-lg border border-ink-300 px-3 py-2 dark:border-ink-600 dark:bg-ink-900"
              value={draft.autoDeadlineMinutesBefore}
              onChange={(e) =>
                setDraft({ ...draft, autoDeadlineMinutesBefore: Number(e.target.value) })
              }
              aria-label="Minuti prima del calcio d'inizio"
            />
            <span className="font-medium">minuti prima della prima partita</span>
          </label>
        )}

        <label className="flex cursor-pointer items-start gap-3">
          <input
            type="checkbox"
            className="mt-1 h-5 w-5 accent-panda-700"
            checked={draft.autoScoring}
            onChange={(e) => setDraft({ ...draft, autoScoring: e.target.checked })}
          />
          <span>
            <span className="font-semibold">Calcolo e pubblicazione automatici</span>
            <span className="block text-sm text-ink-600 dark:text-ink-300">
              Quando l’ultima partita è finita e i risultati sono arrivati, punteggi e classifica
              vengono calcolati e pubblicati da soli (i giocatori ricevono la notifica). Puoi
              comunque ricalcolare o correggere dopo.
            </span>
          </span>
        </label>

        <label className="flex cursor-pointer items-start gap-3">
          <input
            type="checkbox"
            className="mt-1 h-5 w-5 accent-panda-700"
            checked={draft.autoCreate}
            onChange={(e) => setDraft({ ...draft, autoCreate: e.target.checked })}
          />
          <span>
            <span className="font-semibold">Creazione automatica della prossima giornata</span>
            <span className="block text-sm text-ink-600 dark:text-ink-300">
              Pubblicata una giornata, la prossima del campionato viene messa in cantiere da sola
              (e si apre e chiude con le regole qui sopra): la lega corre tutta la stagione senza
              interventi. Richiede la chiusura automatica. Puoi sempre creare giornate a mano.
            </span>
          </span>
        </label>
      </div>

      {dirty && (
        <div className="mt-4">
          <Button
            loading={saving}
            loadingText="Salvataggio…"
            onClick={async () => {
              // Spegnere un'automazione ha conseguenze serie (tutto a mano,
              // giornate che restano bloccate): chiedi conferma esplicita.
              const disabling =
                (automation.autoDeadline && !draft.autoDeadline) ||
                (automation.autoScoring && !draft.autoScoring) ||
                (automation.autoCreate && !draft.autoCreate);
              if (
                disabling &&
                !window.confirm(
                  'Sicuro di voler disabilitare le automazioni?\n\nSenza, la lega va avanti solo a mano: aprire e chiudere i pronostici, calcolare e pubblicare la classifica toccherà a un amministratore, a ogni giornata. Se un passaggio viene dimenticato, la giornata resta bloccata.',
                )
              ) {
                return;
              }
              setError(null);
              setSaving(true);
              try {
                await api(`/leagues/${leagueId}/automation`, { method: 'PUT', body: draft });
                announce('Automazioni aggiornate.');
                queryClient.invalidateQueries({ queryKey: ['leagues', leagueId] });
              } catch (err) {
                const msg = err instanceof ApiError ? err.message : 'Salvataggio non riuscito.';
                setError(msg);
                announce(`Errore: ${msg}`, 'assertive');
              } finally {
                setSaving(false);
              }
            }}
          >
            Salva automazioni
          </Button>
        </div>
      )}
    </section>
  );
}

/**
 * Cruscotto admin di lega (lineeguida §43): stato consegne della giornata
 * corrente con percentuale e chi manca, come testo e barra decorativa.
 */
function AdminSummarySection({ leagueId }: { leagueId: string }) {
  const matchdays = useMatchdays(leagueId);
  const current = matchdays.data?.find((m) => m.state === 'OPEN');
  const status = useMatchdayStatus(leagueId, current?.id ?? '', !!current);

  if (!current || !status.data) return null;

  const { submitted, total, members } = status.data;
  const percent = total > 0 ? Math.round((submitted / total) * 100) : 0;
  const missing = members.filter((m) => !m.hasSubmitted).map((m) => m.displayName);

  return (
    <section
      aria-labelledby="cruscotto-admin"
      className="mt-8 rounded-2xl border border-panda-600 p-5 dark:border-panda-400"
    >
      <h2 id="cruscotto-admin" className="text-xl font-bold tracking-tight">
        Giornata {current.number} — amministrazione
      </h2>
      <p role="status" className="mt-2 text-lg">
        <strong>
          {submitted} su {total}
        </strong>{' '}
        partecipanti hanno giocato ({percent}%).
      </p>
      <div
        aria-hidden="true"
        className="mt-2 h-2.5 w-full max-w-md overflow-hidden rounded-full bg-ink-200 dark:bg-ink-700"
      >
        <div className="h-full rounded-full bg-panda-600" style={{ width: `${percent}%` }} />
      </div>
      {missing.length > 0 && (
        <p className="mt-3 text-ink-700 dark:text-ink-200">
          Non hanno ancora giocato: <strong>{missing.join(', ')}</strong>.
        </p>
      )}
      {current.deadline && (
        <p className="mt-1 text-sm text-ink-600 dark:text-ink-300">
          Alla chiusura chi manca riceverà la colonna rossa.
        </p>
      )}
      <p className="mt-3">
        <Link
          to={`/leghe/${leagueId}/giornate/${current.id}`}
          className="font-medium text-panda-700 underline dark:text-panda-300"
        >
          Gestisci la giornata
        </Link>
      </p>
    </section>
  );
}

interface DisputeRow {
  id: string;
  reason: string;
  description: string;
  status: 'OPEN' | 'APPROVED' | 'REJECTED';
  matchdayNumber: number | null;
  author: string;
  resolutionNote: string | null;
  createdAt: string;
}

/** Contestazioni (lineeguida §39): l'admin vede tutte e le risolve; il giocatore le proprie. */
function DisputesSection({ leagueId, isAdmin }: { leagueId: string; isAdmin: boolean }) {
  const { announce } = useAnnouncer();
  const queryClient = useQueryClient();
  const disputes = useQuery({
    queryKey: ['leagues', leagueId, 'disputes'],
    queryFn: () => api<{ disputes: DisputeRow[] }>(`/leagues/${leagueId}/disputes`).then((r) => r.disputes),
    enabled: !!leagueId,
  });
  const [resolving, setResolving] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  if (!disputes.data || disputes.data.length === 0) return null;

  const resolve = async (dispute: DisputeRow, status: 'APPROVED' | 'REJECTED') => {
    setError(null);
    if (note.trim().length < 3) {
      setError('Aggiungi una nota di risoluzione: verrà inviata all’autore.');
      announce('Errore: aggiungi una nota di risoluzione.', 'assertive');
      return;
    }
    setSaving(true);
    try {
      await api(`/leagues/${leagueId}/disputes/${dispute.id}/resolve`, {
        method: 'POST',
        body: { status, note: note.trim() },
      });
      announce(`Contestazione "${dispute.reason}" ${status === 'APPROVED' ? 'accolta' : 'respinta'}.`);
      setResolving(null);
      setNote('');
      queryClient.invalidateQueries({ queryKey: ['leagues', leagueId, 'disputes'] });
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Operazione non riuscita.';
      setError(msg);
      announce(`Errore: ${msg}`, 'assertive');
    } finally {
      setSaving(false);
    }
  };

  const statusLabel: Record<DisputeRow['status'], string> = {
    OPEN: 'Aperta',
    APPROVED: 'Accolta',
    REJECTED: 'Respinta',
  };

  return (
    <section aria-labelledby="contestazioni" className="mt-10">
      <h2 id="contestazioni" className="text-2xl font-bold tracking-tight">
        Contestazioni
        {isAdmin && disputes.data.some((d) => d.status === 'OPEN') && (
          <span className="ml-3 rounded-full bg-amber-100 px-3 py-1 text-sm font-semibold text-warning dark:bg-ink-800">
            {disputes.data.filter((d) => d.status === 'OPEN').length} da esaminare
          </span>
        )}
      </h2>
      {error && (
        <p role="alert" className="mt-3 font-medium text-danger">{error}</p>
      )}
      <ul className="mt-4 flex flex-col divide-y divide-ink-200 rounded-2xl border border-ink-200 dark:divide-ink-800 dark:border-ink-800">
        {disputes.data.map((d) => (
          <li key={d.id} className="flex flex-col gap-2 px-4 py-3">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h3 className="font-semibold">
                {d.reason}
                {d.matchdayNumber != null && ` — Giornata ${d.matchdayNumber}`}
              </h3>
              <span
                className={`text-sm font-semibold ${
                  d.status === 'OPEN'
                    ? 'text-warning'
                    : d.status === 'APPROVED'
                      ? 'text-success'
                      : 'text-ink-600 dark:text-ink-300'
                }`}
              >
                {statusLabel[d.status]}
              </span>
            </div>
            <p className="text-sm text-ink-600 dark:text-ink-300">Da {d.author}</p>
            <p className="text-ink-800 dark:text-ink-100">{d.description}</p>
            {d.resolutionNote && (
              <p className="text-ink-700 dark:text-ink-200">
                <strong>Risposta dell’amministratore:</strong> {d.resolutionNote}
              </p>
            )}
            {isAdmin && d.status === 'OPEN' && (
              <div className="mt-1">
                {resolving === d.id ? (
                  <div className="flex flex-wrap items-end gap-3">
                    <div className="flex flex-1 flex-col gap-1">
                      <label htmlFor={`note-${d.id}`} className="font-medium">
                        Nota di risoluzione <span className="text-danger">*</span>
                      </label>
                      <input
                        id={`note-${d.id}`}
                        className="rounded-lg border border-ink-300 px-3 py-2 dark:border-ink-600 dark:bg-ink-900"
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                      />
                    </div>
                    <Button onClick={() => resolve(d, 'APPROVED')} loading={saving}>
                      Approva
                    </Button>
                    <Button variant="danger" onClick={() => resolve(d, 'REJECTED')} loading={saving}>
                      Respingi
                    </Button>
                  </div>
                ) : (
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setResolving(d.id);
                      setNote('');
                      setError(null);
                    }}
                  >
                    Esamina
                    <span className="visually-hidden"> la contestazione {d.reason}</span>
                  </Button>
                )}
              </div>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

function TopScorerSection({ leagueId, isAdmin }: { leagueId: string; isAdmin: boolean }) {
  const { announce } = useAnnouncer();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['leagues', leagueId, 'topscorer'],
    queryFn: () =>
      api<{
        mine: { playerName: string } | null;
        locked: boolean;
        revealed: Array<{ displayName: string; playerName: string }>;
      }>(`/leagues/${leagueId}/topscorer`),
    enabled: !!leagueId,
  });
  const [playerName, setPlayerName] = useState('');
  const [actualWinner, setActualWinner] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  if (!query.data) return null;
  const { mine, locked, revealed } = query.data;

  return (
    <section aria-labelledby="capocannoniere" className="mt-10">
      <h2 id="capocannoniere" className="text-2xl font-bold tracking-tight">
        Capocannoniere
      </h2>
      <p className="mt-1 text-ink-700 dark:text-ink-200">
        Pronostico segreto di inizio campionato: nessuno vede il tuo finché non viene rivelato a
        fine stagione.
      </p>

      {error && (
        <p role="alert" className="mt-3 font-medium text-danger">{error}</p>
      )}

      {mine && (
        <p role="status" className="mt-3 font-medium">
          Il tuo pronostico: <strong>{mine.playerName}</strong>
          {locked ? ' (non più modificabile: il campionato è iniziato)' : ''}
        </p>
      )}

      {!locked && (
        <form
          noValidate
          className="mt-3 flex max-w-md flex-col gap-3 sm:flex-row sm:items-end"
          onSubmit={async (e) => {
            e.preventDefault();
            setError(null);
            if (playerName.trim().length < 2) {
              setError('Inserisci il nome del giocatore.');
              return;
            }
            setSaving(true);
            try {
              await api(`/leagues/${leagueId}/topscorer`, {
                method: 'PUT',
                body: { playerName: playerName.trim() },
              });
              announce('Pronostico capocannoniere salvato. Resterà segreto.');
              setPlayerName('');
              queryClient.invalidateQueries({ queryKey: ['leagues', leagueId, 'topscorer'] });
            } catch (err) {
              setError(err instanceof ApiError ? err.message : 'Errore nel salvataggio.');
            } finally {
              setSaving(false);
            }
          }}
        >
          <div className="flex-1">
            <TextField
              label={mine ? 'Cambia il tuo pronostico' : 'Chi sarà il capocannoniere?'}
              description="Nome e cognome del giocatore, es. Lautaro Martínez."
              value={playerName}
              onChange={(e) => setPlayerName(e.target.value)}
            />
          </div>
          <Button type="submit" loading={saving} loadingText="Salvataggio…">
            {mine ? 'Aggiorna' : 'Salva'}
          </Button>
        </form>
      )}

      {revealed.length > 0 && (
        <div className="mt-4">
          <h3 className="font-semibold">Pronostici rivelati</h3>
          <ul className="mt-2 flex flex-col divide-y divide-ink-200 rounded-2xl border border-ink-200 dark:divide-ink-800 dark:border-ink-800">
            {revealed.map((r, i) => (
              <li key={i} className="flex justify-between gap-3 px-4 py-2">
                <span>{r.displayName}</span>
                <span className="font-medium">{r.playerName}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {isAdmin && locked && revealed.length === 0 && (
        <form
          noValidate
          className="mt-5 flex max-w-md flex-col gap-3 rounded-2xl border border-ink-200 p-4 sm:flex-row sm:items-end dark:border-ink-800"
          onSubmit={async (e) => {
            e.preventDefault();
            setError(null);
            if (actualWinner.trim().length < 2) {
              setError('Inserisci il nome del capocannoniere reale.');
              return;
            }
            setSaving(true);
            try {
              const res = await api<{ message: string }>(`/leagues/${leagueId}/topscorer/award`, {
                method: 'POST',
                body: { actualTopScorers: [actualWinner.trim()] },
              });
              announce(res.message);
              setActualWinner('');
              queryClient.invalidateQueries({ queryKey: ['leagues', leagueId, 'topscorer'] });
              queryClient.invalidateQueries({ queryKey: ['leagues', leagueId, 'standings'] });
            } catch (err) {
              setError(err instanceof ApiError ? err.message : 'Errore nella premiazione.');
            } finally {
              setSaving(false);
            }
          }}
        >
          <div className="flex-1">
            <TextField
              label="Premiazione (amministrazione)"
              description="A fine campionato: inserisci il capocannoniere reale per rivelare i pronostici e assegnare il premio."
              value={actualWinner}
              onChange={(e) => setActualWinner(e.target.value)}
            />
          </div>
          <Button type="submit" variant="secondary" loading={saving} loadingText="Premiazione…">
            Premia
          </Button>
        </form>
      )}
    </section>
  );
}

function MembersSection({ leagueId, isAdmin }: { leagueId: string; isAdmin: boolean }) {
  const members = useLeagueMembers(leagueId);
  const updateMember = useUpdateMember(leagueId);
  const { announce } = useAnnouncer();
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [renameError, setRenameError] = useState<string | null>(null);

  const startRename = (memberId: string, currentName: string) => {
    setRenamingId(memberId);
    setNewName(currentName);
    setRenameError(null);
  };

  const saveRename = async (memberId: string) => {
    const trimmed = newName.trim();
    if (trimmed.length === 0) {
      setRenameError('Il nome non può essere vuoto.');
      return;
    }
    try {
      await updateMember.mutateAsync({ memberId, botName: trimmed });
      announce(`Bot rinominato in ${trimmed}. Il nuovo nome appare ovunque, anche nelle giornate passate.`);
      setRenamingId(null);
    } catch (err) {
      setRenameError(err instanceof ApiError ? err.message : 'Errore nel salvataggio del nome.');
    }
  };

  return (
    <section aria-labelledby="partecipanti" className="mt-10">
      <h2 id="partecipanti" className="text-2xl font-bold tracking-tight">
        Partecipanti
      </h2>
      {members.isLoading && (
        <p role="status" className="mt-3">
          Caricamento partecipanti…
        </p>
      )}
      {members.data && (
        <ul className="mt-4 flex flex-col divide-y divide-ink-200 rounded-2xl border border-ink-200 dark:divide-ink-800 dark:border-ink-800">
          {members.data.map((m) => (
            <li key={m.id} className="px-4 py-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <span className="font-medium">
                  {m.displayName}
                  {m.isBot && (
                    <span className="ml-2 rounded-full bg-panda-100 px-2 py-0.5 text-xs font-semibold text-panda-900 dark:bg-panda-900 dark:text-panda-100">
                      Bot
                    </span>
                  )}
                </span>
                <span className="flex items-center gap-3 text-sm text-ink-600 dark:text-ink-300">
                  {m.role === 'ADMIN' ? 'Amministratore' : 'Giocatore'}
                  {m.status === 'PENDING' && ' · In attesa'}
                  {m.status === 'SUSPENDED' && ' · Sospeso'}
                  {isAdmin && m.isBot && renamingId !== m.id && (
                    <Button
                      variant="secondary"
                      onClick={() => startRename(m.id, m.displayName)}
                      aria-label={`Rinomina il bot ${m.displayName}`}
                    >
                      Rinomina
                    </Button>
                  )}
                </span>
              </div>
              {renamingId === m.id && (
                <form
                  noValidate
                  className="mt-3 flex max-w-md flex-col gap-3 sm:flex-row sm:items-end"
                  onSubmit={(e) => {
                    e.preventDefault();
                    void saveRename(m.id);
                  }}
                >
                  <div className="flex-1">
                    <TextField
                      label="Nuovo nome del bot"
                      description="Vale solo per questa lega."
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      maxLength={50}
                      autoFocus
                      {...(renameError ? { error: renameError } : {})}
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button type="submit" loading={updateMember.isPending} loadingText="Salvataggio…">
                      Salva
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => {
                        setRenamingId(null);
                        setRenameError(null);
                      }}
                    >
                      Annulla
                    </Button>
                  </div>
                </form>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function InvitesSection({ leagueId, leagueName }: { leagueId: string; leagueName: string }) {
  const { announce } = useAnnouncer();
  const invites = useLeagueInvites(leagueId, true);
  const permanent = usePermanentInvite(leagueId, true);
  const createInvite = useCreateInvite(leagueId);
  const revokeInvite = useRevokeInvite(leagueId);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  const permanentInvite = permanent.data;
  const inviteUrl = (code: string) => `${window.location.origin}/entra/${code}`;

  const copyLink = async (code: string) => {
    try {
      await navigator.clipboard.writeText(inviteUrl(code));
      setCopiedCode(code);
      announce('Link di invito copiato negli appunti.');
      setTimeout(() => setCopiedCode(null), 3000);
    } catch {
      announce('Impossibile copiare automaticamente. Il link è mostrato accanto al codice.', 'assertive');
    }
  };

  const shareLink = async (code: string) => {
    const url = inviteUrl(code);
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Unisciti a ${leagueName} su PandaCalcio`,
          text: `Entra nella mia lega "${leagueName}" su PandaCalcio!`,
          url,
        });
      } catch {
        // condivisione annullata dall'utente: nessun errore da mostrare
      }
    } else {
      await copyLink(code);
    }
  };

  return (
    <section aria-labelledby="inviti" className="mt-10">
      <h2 id="inviti" className="text-2xl font-bold tracking-tight">
        Inviti
      </h2>

      <div className="mt-4 rounded-2xl border-2 border-panda-300 bg-panda-50 p-4 dark:border-panda-700 dark:bg-panda-950">
        <h3 className="text-lg font-bold">Link di invito della lega</h3>
        <p className="mt-1 text-ink-700 dark:text-ink-200">
          Sempre pronto da condividere (anche su WhatsApp): non scade e vale finché ci sono posti liberi.
        </p>
        {permanent.isLoading && (
          <p role="status" className="mt-3">
            Preparazione del link di invito…
          </p>
        )}
        {permanent.isError && (
          <p role="alert" className="mt-3 font-medium text-danger">
            Impossibile recuperare il link di invito. Ricarica la pagina per riprovare.
          </p>
        )}
        {permanentInvite && (
          <>
            <p className="mt-3">
              Codice: <strong className="font-mono text-xl tracking-widest">{permanentInvite.code}</strong>
            </p>
            <p className="mt-1 break-all text-sm text-ink-600 dark:text-ink-300">
              {inviteUrl(permanentInvite.code)}
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button onClick={() => copyLink(permanentInvite.code)}>
                {copiedCode === permanentInvite.code ? 'Copiato!' : 'Copia link'}
                <span className="visually-hidden"> di invito della lega</span>
              </Button>
              <Button variant="secondary" onClick={() => shareLink(permanentInvite.code)}>
                Condividi
                <span className="visually-hidden"> il link di invito della lega</span>
              </Button>
            </div>
          </>
        )}
      </div>

      <div className="mt-6">
        <p className="text-ink-700 dark:text-ink-200">
          Se ti serve un invito separato (es. da revocare in seguito), creane uno extra.
        </p>
        <div className="mt-3">
          <Button
            variant="secondary"
            onClick={async () => {
              const res = await createInvite.mutateAsync({ maxUses: 20, requiresApproval: false });
              announce(`Invito creato con codice ${res.invite.code}.`);
            }}
            loading={createInvite.isPending}
            loadingText="Creazione invito…"
          >
            Crea un invito extra
          </Button>
        </div>
      </div>

      {invites.data && invites.data.length > 0 && (
        <div className="mt-6 overflow-x-auto">
        <table className="w-full border-collapse text-left">
          <caption className="visually-hidden">
            Elenco degli inviti della lega con codice, utilizzi e azioni
          </caption>
          <thead>
            <tr className="border-b-2 border-ink-300 dark:border-ink-600">
              <th scope="col" className="py-2 pr-3">
                Codice
              </th>
              <th scope="col" className="py-2 pr-3">
                Utilizzi
              </th>
              <th scope="col" className="py-2 pr-3">
                Stato
              </th>
              <th scope="col" className="py-2">
                Azioni
              </th>
            </tr>
          </thead>
          <tbody>
            {invites.data.map((inv) => (
              <tr key={inv.id} className="border-b border-ink-200 dark:border-ink-800">
                <td className="py-3 pr-3 font-mono font-semibold">{inv.code}</td>
                <td className="py-3 pr-3">
                  {inv.maxUses >= 1000
                    ? `${inv.usedCount} (senza limite)`
                    : `${inv.usedCount} di ${inv.maxUses}`}
                </td>
                <td className="py-3 pr-3">
                  {inv.status === 'ACTIVE' && 'Attivo'}
                  {inv.status === 'REVOKED' && 'Revocato'}
                  {inv.status === 'EXHAUSTED' && 'Esaurito'}
                  {inv.status === 'EXPIRED' && 'Scaduto'}
                </td>
                <td className="py-3">
                  {inv.status === 'ACTIVE' && (
                    <span className="flex flex-wrap gap-2">
                      <Button variant="secondary" onClick={() => copyLink(inv.code)}>
                        {copiedCode === inv.code ? 'Copiato!' : 'Copia link'}
                        <span className="visually-hidden"> per il codice {inv.code}</span>
                      </Button>
                      <Button
                        variant="danger"
                        onClick={async () => {
                          await revokeInvite.mutateAsync(inv.id);
                          announce(`Invito ${inv.code} revocato.`);
                        }}
                      >
                        Revoca
                        <span className="visually-hidden"> l’invito {inv.code}</span>
                      </Button>
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
    </section>
  );
}
